import { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import type { Student } from '../api/students';
import { dismissStudent } from '../api/students';
import { getWaitTimeInfo, formatWaitTime } from '../utils/waitTime';

interface StudentCardProps {
  student: Student;
  onDismissed: () => void;
  canDismiss?: boolean;
}

export default function StudentCard({ student, onDismissed, canDismiss = true }: StudentCardProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDismiss = async () => {
    setIsLoading(true);
    try {
      await dismissStudent(student.id);
      onDismissed();
    } catch (err) {
      console.error('Failed to dismiss student:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isDismissed = Boolean(student.dismissed_at);
  const waitTimeInfo = !isDismissed ? getWaitTimeInfo(student) : null;

  return (
    <Card
      elevation={2}
      sx={{
        borderLeft: 4,
        borderColor: isDismissed
          ? 'success.main'
          : waitTimeInfo?.priority === 'urgent'
          ? '#C62828'
          : waitTimeInfo?.priority === 'moderate'
          ? '#F57C00'
          : 'primary.main',
        bgcolor: isDismissed ? 'grey.50' : 'background.paper',
        position: 'relative',
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {/* Header: Name and Grade */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              {student.first_name} {student.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Grade {student.grade}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {waitTimeInfo && waitTimeInfo.priority === 'urgent' && (
              <Chip
                icon={<PriorityHighIcon sx={{ fontSize: 16 }} />}
                label="Urgent"
                size="small"
                sx={{
                  bgcolor: waitTimeInfo.backgroundColor,
                  color: waitTimeInfo.color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            )}
            <Chip
              icon={student.pickup_type === 'car' ? <DirectionsCarIcon /> : <DirectionsWalkIcon />}
              label={student.pickup_type === 'car' ? 'Car' : 'Walker'}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Time Information */}
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Checked In:</strong>{' '}
              {format(new Date(student.checked_in_at), 'h:mm a')}
            </Typography>
          </Box>

          {!isDismissed && waitTimeInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`Waiting: ${formatWaitTime(waitTimeInfo.minutes)}`}
                size="small"
                sx={{
                  bgcolor: waitTimeInfo.backgroundColor,
                  color: waitTimeInfo.color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          )}

          {isDismissed && student.dismissed_at && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
              <Typography variant="body2" color="success.main">
                <strong>Dismissed:</strong>{' '}
                {format(new Date(student.dismissed_at), 'h:mm a')}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        {isDismissed ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Dismissed"
            color="success"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        ) : canDismiss ? (
          <LoadingButton
            variant="dismiss"
            fullWidth
            size="large"
            loading={isLoading}
            onClick={handleDismiss}
          >
            Mark as Dismissed
          </LoadingButton>
        ) : (
          <Chip
            icon={<AccessTimeIcon />}
            label="Waiting"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        )}
      </CardActions>
    </Card>
  );
}
